import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import translations from '../constants/translations.json';
import { useDataContext } from '../context/data';
import LanguageSelectorDropdown from './LanguageSelectorDropdown';

const MobileNavbar = ({
  isLoggedIn,
  isPremium,
  showWallet,
  onAuthAction,
  onAccountAction,
  onQrAction,
}) => {
  const { language } = useDataContext();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  // HeyWaitr+ sub-menu accordion state
  const [plusOpen, setPlusOpen] = useState(false);

  // Close sidebar when navigating or on screen resize above lg breakpoint
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle body scroll when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
      setPlusOpen(false);
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  return (
    <div className="block lg:hidden">
      <div className="flex justify-end space-x-2">
        <LanguageSelectorDropdown />

        {/* Hamburger Button */}
        <button
          className="flex items-center p-2 focus:outline-none"
          onClick={toggleSidebar}
          aria-label="Toggle menu"
        >
          <i className="fa fa-bars" />
        </button>
      </div>

      {/* Overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[110]" onClick={toggleSidebar} />
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 right-0 w-64 h-full bg-white z-[120] shadow-lg transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex justify-between items-center p-2 border-b border-[#f5f5f5]">
          {isPremium ? (
            <span className="inline-flex items-center gap-1.5 bg-[#e5ca17]/15 text-[#8a7400] border border-[#e5ca17]/40 rounded-full px-2.5 py-1 text-[11px] font-bold ml-1">
              <i className="fa fa-crown text-[#e5ca17]" />
              heywaitr+
            </span>
          ) : (
            <span />
          )}
          <button
            onClick={toggleSidebar}
            className="p-2 focus:outline-none"
            aria-label="Close menu"
          >
            <i className="fa fa-times" />
          </button>
        </div>

        <div className="flex flex-col">
          <Link
            to="/"
            onClick={() => handleNavigation('/')}
            className="flex items-center py-3 px-4 hover:bg-gray-100 text-sm hover:text-secondary rounded-md"
          >
            <i className="fa fa-home mr-3" />
            <span>{translations[language]?.home_page}</span>
          </Link>

          <Link
            to="/"
            onClick={() => handleNavigation('/')}
            className="flex items-center py-3 px-4 hover:bg-gray-100 text-sm hover:text-secondary rounded-md"
          >
            <i className="fa fa-utensils mr-3" />
            <span>{translations[language]?.restaurants}</span>
          </Link>

          {isLoggedIn && (
            <Link
              to="/orders"
              onClick={() => handleNavigation('/orders')}
              className="flex items-center py-3 px-4 hover:bg-gray-100 text-sm hover:text-secondary rounded-md"
            >
              <i className="fa fa-receipt mr-3" />
              <span>{translations[language]?.orders}</span>
            </Link>
          )}

          {showWallet && (
            <Link
              to="/wallet"
              onClick={() => handleNavigation('/wallet')}
              className="flex items-center py-3 px-4 hover:bg-gray-100 text-sm hover:text-secondary rounded-md"
            >
              <i className="fa fa-wallet mr-3" />
              <span>{translations[language]?.wallet || 'Wallet'}</span>
            </Link>
          )}

          {/* HeyWaitr+ — subscribers get an expandable group of sub-pages;
              non-subscribers get a plain link. */}
          {isPremium ? (
            <>
              <button
                onClick={() => setPlusOpen((v) => !v)}
                aria-expanded={plusOpen}
                className="flex items-center py-3 px-4 hover:bg-gray-100 text-sm hover:text-secondary rounded-md text-left w-full"
              >
                <i className="fa fa-bolt mr-3" />
                <span className="flex-1">
                  heywaitr<span className="text-[#e5ca17]">+</span>
                </span>
                <i
                  className={`fa fa-chevron-down text-[11px] text-gray-400 transition-transform duration-200 ${
                    plusOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {plusOpen && (
                <div className="ml-5 border-l border-[#f0f0f0] flex flex-col">
                  <Link
                    to="/heywaitr-plus"
                    onClick={() => handleNavigation('/heywaitr-plus')}
                    className="flex items-center py-2.5 px-4 hover:bg-gray-100 text-sm hover:text-secondary rounded-md"
                  >
                    <i className="fa fa-tags mr-3 text-[#e5ca17]" />
                    <span>{translations[language]?.deals_nav || 'Deals'}</span>
                  </Link>

                  <Link
                    to="/perks"
                    onClick={() => handleNavigation('/perks')}
                    className="flex items-center py-2.5 px-4 hover:bg-gray-100 text-sm hover:text-secondary rounded-md"
                  >
                    <i className="fa fa-gift mr-3 text-[#e5ca17]" />
                    <span>{translations[language]?.perks_nav || 'Perks'}</span>
                  </Link>
                </div>
              )}
            </>
          ) : (
            <Link
              to="/heywaitr-plus"
              onClick={() => handleNavigation('/heywaitr-plus')}
              className="flex items-center py-3 px-4 hover:bg-gray-100 text-sm hover:text-secondary rounded-md"
            >
              <i className="fa fa-bolt mr-3" />
              <span>
                heywaitr<span className="text-[#e5ca17]">+</span>
              </span>
            </Link>
          )}

          <button
            onClick={() => {
              setIsOpen(false);
              onQrAction?.();
            }}
            className="flex items-center py-3 px-4 hover:bg-gray-100 text-sm rounded-md text-left w-full"
          >
            <i className="fa fa-qrcode mr-3" />
            <span>{translations[language]?.dine_together_scan_qr || 'Scan QR'}</span>
          </button>

          <div className="border-t border-[#f5f5f5] my-2"></div>

          <button
            onClick={() => {
              if (isLoggedIn) {
                setIsOpen(false);
              }

              onAccountAction();
            }}
            className="flex items-center py-3 px-4 hover:bg-gray-100 text-sm hover:text-secondary rounded-md text-left"
          >
            <i className="fa fa-user-circle mr-3" />
            <span>
              {isLoggedIn
                ? `${translations[language]?.my_profile}`
                : `${translations[language]?.create_account}`}
            </span>
          </button>

          <button
            onClick={() => {
              if (isLoggedIn) {
                setIsOpen(false);
              }

              onAuthAction();
            }}
            className="flex items-center py-3 px-4 hover:bg-gray-100 text-sm hover:text-secondary rounded-md text-left"
          >
            <i className={`mr-3 fa ${isLoggedIn ? 'fa-sign-out-alt' : 'fa-sign-in-alt'}`} />
            <span>
              {isLoggedIn ? translations[language]?.log_out : translations[language]?.log_in}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileNavbar;
